import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Form, Input, Modal } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUserRequestEmailUpdate } from "../../../../common/api/queries";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { ModalSizes } from "../../../../common/constants";
import { AgentUserProfile, ClientUserProfile, RequestUserEmailUpdate } from "../../types";

interface Props {
  open: boolean;
  user: AgentUserProfile | ClientUserProfile;
  onFormCancel: () => void;
}

export const RequestEmailUpdateForm = ({ open, user, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<RequestUserEmailUpdate>();

  const requestEmailUpdate = useUserRequestEmailUpdate();

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        optimisticLockVersion: user.optimisticLockVersion,
        password: undefined,
      });
    }
  }, [open, user, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        requestEmailUpdate.mutate(
          { id: user.id, object: values },
          {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "user.attrs", error.violations),
          },
        );
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={open}
      title={t("user.titles.updateEmail")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={requestEmailUpdate.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="requestEmailUpdateForm">
        <HiddenInput name="optimisticLockVersion" />

        <Form.Item
          name="emailToConfirm"
          label={t("user.attrs.emailToConfirm")}
          rules={[validations.notBlank, validations.size(1, 254), validations.email]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="password" label={t("user.attrs.previousPassword")} rules={[validations.notBlank]}>
          <Input.Password />
        </Form.Item>
      </Form>
    </Modal>
  );
};
